import { createStore, del, entries, get, set } from 'idb-keyval'
import type { CreateExpenseDto } from '@fp3/shared'

// storage primitives for the offline outbox. deliberately free of DOM and React imports:
// the service worker imports this module too, and a service worker has no `window`.

export type OutboxStatus = 'pending' | 'syncing' | 'failed'

export interface OutboxEntry {
  // the idempotency key sent to the API. minted once when the expense is queued, so
  // replaying an entry can never produce a second expense.
  clientRequestId: string
  dto: CreateExpenseDto
  createdAt: string
  status: OutboxStatus
  attempts: number
  // the server's reason for refusing the write; only set on `failed`
  lastError?: string
}

// each idb-keyval store gets its OWN database name. `createStore` opens its database at
// version 1 and creates a single object store in the upgrade; pointing a second store at
// the same database finds version 1 already present, so its upgrade never runs and every
// operation against it fails. sharing a name silently breaks whichever store opens second.
const DB_NAME = 'fp3-expense-outbox'
const STORE_NAME = 'entries'

// a lazily-created handle. `createStore` opens an IndexedDB connection as a side effect,
// so it must not run at module scope in environments (jsdom, SSR) that lack indexedDB.
let store: ReturnType<typeof createStore> | undefined
const outboxStore = () => (store ??= createStore(DB_NAME, STORE_NAME))

// IndexedDB is missing in jsdom and can be blocked outright by a browser's privacy mode
export const isOutboxStorageAvailable = (): boolean => typeof indexedDB !== 'undefined'

// entries oldest-first, so a drain replays in the order the user entered them.
// reads degrade to an empty queue without storage — nothing could have been stored, so
// that is the accurate answer rather than a swallowed failure.
export async function readOutbox(): Promise<OutboxEntry[]> {
  if (!isOutboxStorageAvailable()) return []
  const rows = await entries<string, OutboxEntry>(outboxStore())
  return rows.map(([, entry]) => entry).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

// writes deliberately do NOT degrade quietly: if the expense cannot be persisted, the
// caller must fail so the user is told, rather than being shown a queue that is a fiction.
export async function writeOutboxEntry(entry: OutboxEntry): Promise<void> {
  if (!isOutboxStorageAvailable()) {
    throw new Error('Offline storage is unavailable, so this expense cannot be queued')
  }
  await set(entry.clientRequestId, entry, outboxStore())
}

export async function deleteOutboxEntry(clientRequestId: string): Promise<void> {
  if (!isOutboxStorageAvailable()) return
  await del(clientRequestId, outboxStore())
}

// the access token, mirrored where the service worker can read it. a worker has no access
// to localStorage, and background sync (android) runs with no page open, so replaying
// there needs its own copy. this does not widen the app's exposure — the token is already
// readable by any same-origin script via localStorage — but it must be cleared on logout,
// which `clearReplayToken` does from the auth provider.
const TOKEN_KEY = 'replay-token'

let tokenStore: ReturnType<typeof createStore> | undefined
const replayTokenStore = () => (tokenStore ??= createStore('fp3-replay-auth', 'token'))

// the mirror is a best-effort optimisation for background sync, so all three degrade to
// no-ops without storage: losing it only costs the closed-app replay, never an expense.
export async function saveReplayToken(token: string): Promise<void> {
  if (!isOutboxStorageAvailable()) return
  await set(TOKEN_KEY, token, replayTokenStore())
}

export async function readReplayToken(): Promise<string | undefined> {
  if (!isOutboxStorageAvailable()) return undefined
  return get<string>(TOKEN_KEY, replayTokenStore())
}

export async function clearReplayToken(): Promise<void> {
  if (!isOutboxStorageAvailable()) return
  await del(TOKEN_KEY, replayTokenStore())
}
